import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  ActiveTeamAtom,
  MyTeamsAtom,
  TeamModalAtom,
} from '../../recoil/teamAtoms';
import {
  listMyTeams,
  createTeam,
  joinByCode,
  getTeam,
  leaveTeam,
  deleteTeam,
  removeMember,
  regenerateJoinCode,
  updateTeam,
} from '../../utils/teamApi';

// ===== styled =====
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: ${p => p.theme.bgOverlay};
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  background: ${p => p.theme.bgCard};
  width: 95vw;
  max-width: 720px;
  max-height: 90vh;
  border-radius: ${p => p.theme.radiusLg};
  box-shadow: ${p => p.theme.shadowLg};
  border: 1px solid ${p => p.theme.border};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    max-width: none;
    max-height: none;
    border-radius: 0;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${p => p.theme.accent} 0%, #6d4ba2 100%);
  color: #fff;
  padding: 16px 22px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media screen and (max-width: 480px) {
    padding: 12px 14px;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;

  @media screen and (max-width: 480px) {
    font-size: 14px;
  }
`;

const CloseX = styled.button`
  background: rgba(255,255,255,0.18);
  border: none;
  color: #fff;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;

  &:hover { background: rgba(255,255,255,0.3); }
`;

const Tabs = styled.div`
  display: flex;
  background: ${p => p.theme.bgInput};
  border-bottom: 1px solid ${p => p.theme.border};
  padding: 0 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media screen and (max-width: 480px) {
    padding: 0 6px;
  }
`;

const Tab = styled.button`
  background: transparent;
  border: none;
  border-bottom: 2px solid ${p => p.$active ? p.theme.accent : 'transparent'};
  color: ${p => p.$active ? p.theme.accent : p.theme.textSecondary};
  font-weight: ${p => p.$active ? 700 : 500};
  padding: 12px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: ${p => p.theme.transition};
  white-space: nowrap;

  &:hover { color: ${p => p.theme.accent}; }

  @media screen and (max-width: 480px) {
    padding: 12px 10px;
    font-size: 12px;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 22px;

  @media screen and (max-width: 480px) {
    padding: 14px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  font-size: 13px;
  box-sizing: border-box;
  font-family: inherit;

  &:focus { outline: none; border-color: ${p => p.theme.borderInputFocus}; }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  font-size: 13px;
  resize: vertical;
  min-height: 60px;
  box-sizing: border-box;
  font-family: inherit;

  &:focus { outline: none; border-color: ${p => p.theme.borderInputFocus}; }
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  font-size: 13px;
  box-sizing: border-box;
  cursor: pointer;

  &:focus { outline: none; border-color: ${p => p.theme.borderInputFocus}; }
`;

const PRICE_UNITS = ['원', '천원', '만원', '억원'];

const PrimaryButton = styled.button`
  background: ${p => p.theme.accent};
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: ${p => p.theme.transition};

  &:hover { background: ${p => p.theme.accentHover}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  color: ${p => p.theme.textSecondary};
  padding: 8px 14px;
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover { background: ${p => p.theme.bgHover}; color: ${p => p.theme.text}; }
`;

const DangerButton = styled.button`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.error};
  color: ${p => p.theme.error};
  padding: 7px 12px;
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;

  &:hover { background: #fee2e2; }
`;

const TeamCard = styled.div`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  padding: 14px 16px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TeamRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const TeamName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Badge = styled.span`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${p => p.$role === 'owner' ? p.theme.bgRequired : p.theme.accentLight};
  color: ${p => p.$role === 'owner' ? p.theme.textRequired : p.theme.accent};
`;

const TeamDesc = styled.div`
  font-size: 12px;
  color: ${p => p.theme.textMuted};
`;

const TeamMeta = styled.div`
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  display: flex;
  gap: 12px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ManagePanel = styled.div`
  background: ${p => p.theme.bgInput};
  border-radius: ${p => p.theme.radiusSm};
  padding: 12px;
  margin-top: 6px;
  border: 1px solid ${p => p.theme.border};
`;

const CodeBox = styled.div`
  background: ${p => p.theme.bgCard};
  border: 2px dashed ${p => p.theme.accent};
  border-radius: ${p => p.theme.radiusSm};
  padding: 12px;
  font-family: monospace;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 4px;
  text-align: center;
  color: ${p => p.theme.accent};
  margin-bottom: 8px;
`;

const MemberRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid ${p => p.theme.border};
  font-size: 12px;
  color: ${p => p.theme.text};

  &:first-child { border-top: none; }
`;

const ErrorMsg = styled.div`
  background: #fee2e2;
  color: #b91c1c;
  padding: 8px 12px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 12px;
  margin-bottom: 12px;
`;

const SuccessMsg = styled.div`
  background: #d1fae5;
  color: #047857;
  padding: 8px 12px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 12px;
  margin-bottom: 12px;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: ${p => p.theme.textMuted};
  font-size: 13px;
`;

// ===== Component =====
export default function TeamModal() {
  const [modal, setModal] = useRecoilState(TeamModalAtom);
  const [myTeams, setMyTeams] = useRecoilState(MyTeamsAtom);
  const setActiveTeam = useSetRecoilState(ActiveTeamAtom);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // create form
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createUnit, setCreateUnit] = useState('만원');
  // join form
  const [joinCode, setJoinCode] = useState('');
  // manage panel: team detail cache
  const [managedTeams, setManagedTeams] = useState({}); // team_id -> { team, members }

  const close = useCallback(() => {
    setModal({ open: false, tab: 'mine', manageTeamId: null });
    setError(''); setSuccess('');
    setCreateName(''); setCreateDesc(''); setJoinCode('');
  }, [setModal]);

  const refresh = useCallback(async () => {
    try {
      const r = await listMyTeams();
      setMyTeams(r.teams || []);
    } catch (e) {
      // ignore
    }
  }, [setMyTeams]);

  useEffect(() => {
    if (modal.open) {
      setError(''); setSuccess('');
      refresh();
    }
  }, [modal.open, refresh]);

  if (!modal.open) return null;

  const showMessage = (kind, msg) => {
    if (kind === 'error') { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 3500);
  };

  const handleCreate = async () => {
    if (!createName.trim()) return showMessage('error', '팀 이름을 입력해주세요.');
    setLoading(true);
    try {
      const r = await createTeam(createName.trim(), createDesc.trim(), createUnit);
      showMessage('ok', `"${r.team.name}" 팀이 생성되었습니다. 코드: ${r.team.join_code}`);
      setCreateName(''); setCreateDesc(''); setCreateUnit('만원');
      await refresh();
      setActiveTeam(r.team);
      setModal({ open: true, tab: 'mine', manageTeamId: r.team.team_id });
    } catch (e) {
      showMessage('error', e.message || '생성 실패');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) return showMessage('error', '코드를 입력해주세요.');
    setLoading(true);
    try {
      const r = await joinByCode(joinCode.trim());
      showMessage('ok', r.already_member ? '이미 멤버인 팀입니다.' : `"${r.name}" 팀에 가입되었습니다.`);
      setJoinCode('');
      await refresh();
      // 새로 가입한 팀을 활성으로 설정
      const fresh = await listMyTeams();
      const found = (fresh.teams || []).find(t => t.team_id === r.team_id);
      if (found) setActiveTeam(found);
      setMyTeams(fresh.teams || []);
      setModal({ open: true, tab: 'mine', manageTeamId: null });
    } catch (e) {
      showMessage('error', e.message || '조인 실패');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamDetail = async (team_id) => {
    try {
      const r = await getTeam(team_id);
      setManagedTeams(prev => ({ ...prev, [team_id]: r }));
    } catch (e) {
      showMessage('error', e.message || '팀 상세 로드 실패');
    }
  };

  const toggleManage = (team_id) => {
    if (modal.manageTeamId === team_id) {
      setModal({ ...modal, manageTeamId: null });
    } else {
      setModal({ ...modal, manageTeamId: team_id });
      if (!managedTeams[team_id]) loadTeamDetail(team_id);
    }
  };

  const handleLeave = async (team) => {
    if (!window.confirm(`"${team.name}" 팀에서 탈퇴하시겠습니까?`)) return;
    try {
      await leaveTeam(team.team_id);
      showMessage('ok', '탈퇴되었습니다.');
      setActiveTeam(prev => prev && prev.team_id === team.team_id ? null : prev);
      await refresh();
    } catch (e) {
      showMessage('error', e.message || '탈퇴 실패');
    }
  };

  const handleDeleteTeam = async (team) => {
    if (!window.confirm(`"${team.name}" 팀을 영구 삭제합니다.\n팀의 모든 카테고리, 항목이 함께 삭제됩니다.\n계속하시겠습니까?`)) return;
    try {
      await deleteTeam(team.team_id);
      showMessage('ok', '팀이 해체되었습니다.');
      setActiveTeam(prev => prev && prev.team_id === team.team_id ? null : prev);
      await refresh();
    } catch (e) {
      showMessage('error', e.message || '삭제 실패');
    }
  };

  const handleRemoveMember = async (team_id, user_id, name) => {
    if (!window.confirm(`"${name}" 멤버를 팀에서 제외하시겠습니까?`)) return;
    try {
      await removeMember(team_id, user_id);
      showMessage('ok', '제외되었습니다.');
      await loadTeamDetail(team_id);
    } catch (e) {
      showMessage('error', e.message || '제외 실패');
    }
  };

  const handleRegenerateCode = async (team_id) => {
    if (!window.confirm('새 초대 코드를 발급합니다. 기존 코드는 무효화됩니다. 계속하시겠습니까?')) return;
    try {
      const r = await regenerateJoinCode(team_id);
      showMessage('ok', `새 코드: ${r.join_code}`);
      await loadTeamDetail(team_id);
      await refresh();
    } catch (e) {
      showMessage('error', e.message || '재발급 실패');
    }
  };

  const handleEditTeam = async (team) => {
    const newName = window.prompt('팀 이름', team.name);
    if (!newName || !newName.trim() || newName.trim() === team.name) return;
    try {
      await updateTeam(team.team_id, newName.trim(), team.description || '', team.price_unit || '만원');
      showMessage('ok', '수정되었습니다.');
      await refresh();
      await loadTeamDetail(team.team_id);
    } catch (e) {
      showMessage('error', e.message || '수정 실패');
    }
  };

  const handleChangePriceUnit = async (team, newUnit) => {
    try {
      await updateTeam(team.team_id, team.name, team.description || '', newUnit);
      showMessage('ok', `가격 단위를 "${newUnit}" 으로 변경했습니다.`);
      await refresh();
      await loadTeamDetail(team.team_id);
      // 활성 팀이면 ActiveTeamAtom 도 갱신해서 wizard / 트리관리자가 즉시 반영
      const fresh = await listMyTeams();
      const updated = (fresh.teams || []).find(t => t.team_id === team.team_id);
      if (updated) setActiveTeam(prev => (prev && prev.team_id === team.team_id) ? updated : prev);
      setMyTeams(fresh.teams || []);
    } catch (e) {
      showMessage('error', e.message || '단위 변경 실패');
    }
  };

  return (
    <Backdrop onClick={close}>
      <Container onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>팀 (소속그룹) 관리</Title>
          <CloseX onClick={close}>×</CloseX>
        </Header>

        <Tabs>
          <Tab $active={modal.tab === 'mine'} onClick={() => setModal({ ...modal, tab: 'mine' })}>
            내 팀 ({myTeams.length})
          </Tab>
          <Tab $active={modal.tab === 'create'} onClick={() => setModal({ ...modal, tab: 'create' })}>
            새 팀 만들기
          </Tab>
          <Tab $active={modal.tab === 'join'} onClick={() => setModal({ ...modal, tab: 'join' })}>
            초대 코드로 조인
          </Tab>
        </Tabs>

        <Body>
          {error && <ErrorMsg>{error}</ErrorMsg>}
          {success && <SuccessMsg>{success}</SuccessMsg>}

          {modal.tab === 'mine' && (
            <>
              {myTeams.length === 0 ? (
                <Empty>
                  아직 소속된 팀이 없습니다. <br />
                  새 팀을 만들거나 초대 코드로 조인해보세요.
                </Empty>
              ) : (
                myTeams.map(t => {
                  const detail = managedTeams[t.team_id];
                  const isManaging = modal.manageTeamId === t.team_id;
                  return (
                    <TeamCard key={t.team_id}>
                      <TeamRow>
                        <TeamName>
                          {t.name}
                          <Badge $role={t.my_role}>
                            {t.my_role === 'owner' ? 'OWNER' : 'MEMBER'}
                          </Badge>
                        </TeamName>
                        <ActionsRow>
                          <SecondaryButton onClick={() => toggleManage(t.team_id)}>
                            {isManaging ? '닫기' : '관리'}
                          </SecondaryButton>
                          {t.my_role === 'owner' ? (
                            <DangerButton onClick={() => handleDeleteTeam(t)}>해체</DangerButton>
                          ) : (
                            <DangerButton onClick={() => handleLeave(t)}>탈퇴</DangerButton>
                          )}
                        </ActionsRow>
                      </TeamRow>
                      {t.description && <TeamDesc>{t.description}</TeamDesc>}
                      <TeamMeta>
                        <span>멤버 {t.member_count}명</span>
                        <span>코드: <code>{t.join_code}</code></span>
                      </TeamMeta>

                      {isManaging && (
                        <ManagePanel>
                          {!detail ? (
                            <Empty>로딩 중...</Empty>
                          ) : (
                            <>
                              {t.my_role === 'owner' && (
                                <>
                                  <Label>초대 코드 (멤버 초대 시 공유)</Label>
                                  <CodeBox>{detail.team.join_code}</CodeBox>
                                  <ActionsRow>
                                    <SecondaryButton onClick={() => handleRegenerateCode(t.team_id)}>
                                      코드 재발급
                                    </SecondaryButton>
                                    <SecondaryButton onClick={() => handleEditTeam(detail.team)}>
                                      팀 이름 수정
                                    </SecondaryButton>
                                  </ActionsRow>

                                  <Label style={{ marginTop: 14 }}>가격 단위</Label>
                                  <Select
                                    value={detail.team.price_unit || '만원'}
                                    onChange={(e) => handleChangePriceUnit(detail.team, e.target.value)}
                                  >
                                    {PRICE_UNITS.map(u => (
                                      <option key={u} value={u}>{u}</option>
                                    ))}
                                  </Select>
                                  <p style={{ marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
                                    이 팀의 모든 견적 항목 가격이 이 단위로 입력/표시됩니다. 기존에 입력한 숫자는 유지되니 단위 변경 시 의미가 바뀔 수 있어요.
                                  </p>
                                </>
                              )}
                              {t.my_role !== 'owner' && (
                                <>
                                  <Label>가격 단위</Label>
                                  <div style={{ fontSize: 13, color: '#475569' }}>{detail.team.price_unit || '만원'}</div>
                                </>
                              )}

                              <Label style={{ marginTop: 14 }}>멤버 ({detail.members.length})</Label>
                              {detail.members.map(m => (
                                <MemberRow key={m.user_id}>
                                  <span>
                                    {m.user_name || m.user_account || `User#${m.user_id}`}
                                    {' '}
                                    <Badge $role={m.role} style={{ marginLeft: 6 }}>
                                      {m.role === 'owner' ? 'OWNER' : 'MEMBER'}
                                    </Badge>
                                  </span>
                                  {t.my_role === 'owner' && m.role !== 'owner' && (
                                    <DangerButton
                                      onClick={() => handleRemoveMember(t.team_id, m.user_id, m.user_name || m.user_account)}
                                    >
                                      제외
                                    </DangerButton>
                                  )}
                                </MemberRow>
                              ))}
                            </>
                          )}
                        </ManagePanel>
                      )}
                    </TeamCard>
                  );
                })
              )}
            </>
          )}

          {modal.tab === 'create' && (
            <>
              <FormGroup>
                <Label>팀 이름 *</Label>
                <Input
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="예: ABC회사 견적팀"
                  autoFocus
                />
              </FormGroup>
              <FormGroup>
                <Label>설명 (선택)</Label>
                <Textarea
                  value={createDesc}
                  onChange={(e) => setCreateDesc(e.target.value)}
                  placeholder="이 팀의 목적, 사용 시나리오 등"
                />
              </FormGroup>
              <FormGroup>
                <Label>가격 단위</Label>
                <Select value={createUnit} onChange={(e) => setCreateUnit(e.target.value)}>
                  {PRICE_UNITS.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </Select>
                <p style={{ marginTop: 4, fontSize: 11, color: '#94a3b8' }}>
                  이 팀의 모든 견적 항목 가격이 이 단위로 입력/표시됩니다. 나중에 변경 가능.
                </p>
              </FormGroup>
              <PrimaryButton disabled={loading} onClick={handleCreate}>
                {loading ? '생성 중...' : '팀 만들기'}
              </PrimaryButton>
              <p style={{ marginTop: 12, fontSize: 11, color: '#94a3b8' }}>
                생성 후 자동으로 owner 권한이 부여되고, 8자리 초대 코드가 발급됩니다.
                동료에게 코드를 공유하면 [초대 코드로 조인] 탭에서 가입할 수 있습니다.
              </p>
            </>
          )}

          {modal.tab === 'join' && (
            <>
              <FormGroup>
                <Label>초대 코드 (8자리)</Label>
                <Input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="예: A2B3C4D5"
                  maxLength={8}
                  autoFocus
                  style={{ fontFamily: 'monospace', letterSpacing: 4, fontSize: 16 }}
                />
              </FormGroup>
              <PrimaryButton disabled={loading} onClick={handleJoin}>
                {loading ? '가입 중...' : '팀 조인'}
              </PrimaryButton>
              <p style={{ marginTop: 12, fontSize: 11, color: '#94a3b8' }}>
                팀 owner 가 발급한 8자리 코드를 입력하세요. 가입 후 자동으로 활성 팀이 됩니다.
              </p>
            </>
          )}
        </Body>
      </Container>
    </Backdrop>
  );
}
